import { Bookmark, BookmarkCheck } from "lucide-react";
import { SECTION_TITLES } from "../../copy/section-titles";
import { useMemo, type CSSProperties } from "react";

import type { Chapter, ChapterSlot } from "../events/landing";
import { timezonePlace } from "../events/timezones";
import { usableTimeZone } from "./agenda-schedule";
import { bookmarkTitle } from "./bookmark-title";
import { dayNumbers, sessionWhen, type EventDates } from "./landing-model";
import type { AgendaItem } from "./schemas";
import { useBookmarks } from "./use-bookmarks";

/**
 * The three chapter styles. One layout, two skins: the blocks are the same
 * size and shape in both themes, tall, with the big outlined numeral, and only
 * the surface and colours change.
 *
 * Light is a quiet white card (`bg-card`, a 1px border) with a 5px coloured top
 * edge, purple, navy and then yellow, and the numeral outlined in that colour.
 * There is no outline and no hard shadow, because the hard edge is kept for the
 * main button, the live card and the running event. Yellow is the chapter 3 top
 * edge only, never a fill.
 *
 * Dusk (`dark:`) is the gradient chapters with the lit hairline and halo and a
 * white numeral.
 *
 * `block` is the card, `chip` the numeral's colour, `solid` the filled button on
 * it and `ring` its focus ring, which must read against that fill. Written out
 * in full so Tailwind finds every class.
 */
const LIGHT_CARD =
  "border border-border border-t-[5px] bg-card text-foreground dark:border-edge dark:border-lit-edge dark:border-t-edge dark:border-t-lit-edge dark:shadow-lit dark:text-white";

export const CHAPTER_STYLE: Record<
  ChapterSlot,
  { block: string; chip: string; solid: string; ring: string }
> = {
  1: {
    block: `${LIGHT_CARD} border-t-hb-purple-fill dark:bg-dusk-chapter-1`,
    chip: "text-hb-purple-fill dark:text-white",
    solid:
      "border-hb-purple-fill bg-hb-purple-fill text-white hover:bg-hb-purple-fill/90 dark:border-white dark:bg-white dark:text-hb-dusk-deep dark:hover:bg-white/90",
    ring: "focus-visible:ring-hb-purple-fill dark:focus-visible:ring-white",
  },
  2: {
    block: `${LIGHT_CARD} border-t-hb-blue-fill dark:bg-dusk-chapter-2`,
    chip: "text-hb-blue-fill dark:text-white",
    solid:
      "border-hb-blue-fill bg-hb-blue-fill text-white hover:bg-hb-blue-fill/90 dark:border-white dark:bg-white dark:text-hb-dusk-deep dark:hover:bg-white/90",
    ring: "focus-visible:ring-hb-blue-fill dark:focus-visible:ring-white",
  },
  3: {
    block: `${LIGHT_CARD} border-t-hb-yellow dark:bg-dusk-chapter-3`,
    chip: "text-hb-warm-grey dark:text-white",
    solid:
      "border-hb-warm-grey bg-hb-warm-grey text-white hover:bg-hb-warm-grey/90 dark:border-white dark:bg-white dark:text-hb-dusk-deep dark:hover:bg-white/90",
    ring: "focus-visible:ring-hb-warm-grey dark:focus-visible:ring-white",
  },
};

/**
 * The chapter number, the big outlined numeral in both themes: the fill is
 * knocked out with `text-fill-color`, not `color`, so `currentColor` still
 * names the colour the chapter style gives it and the outline takes it. `size`
 * is the numeral's size, written out so Tailwind finds it.
 */
export const CHAPTER_NUMBER_SIZE = {
  large: "text-9xl",
  medium: "text-8xl",
} as const;

export const CHAPTER_NUMBER_CLASS =
  "font-bold leading-[0.8] [-webkit-text-fill-color:transparent] [-webkit-text-stroke:2px_currentColor]";

/**
 * The title, in the size `chapterTitleFontSize` works out, carried in a custom
 * property.
 */
export const CHAPTER_TITLE_CLASS =
  "font-display text-[length:var(--chapter-title-size)] font-normal leading-none wrap-anywhere";

/**
 * The title's size from its longest word: a line breaks between words, so
 * only a word wider than the block would overflow, and that one also gets
 * `wrap-anywhere` as the last resort.
 */
export function chapterTitleFontSize(title: string): string {
  const longest = Math.max(4, ...title.split(/\s+/).map((word) => word.length));
  return `clamp(2rem, calc((100cqi - 2.5rem) / ${(longest * 0.58).toFixed(2)}), 4.25rem)`;
}

/** The size as the custom property `CHAPTER_TITLE_CLASS` reads. */
export function chapterTitleStyle(title: string): CSSProperties {
  return {
    "--chapter-title-size": chapterTitleFontSize(title),
  } as CSSProperties;
}

/** The time under the title, an outlined pill in the block's text colour. */
export const CHAPTER_WHEN_CLASS =
  "self-start rounded-full border-2 border-current px-3 py-1 text-sm font-bold uppercase tracking-[0.08em]";

const FOCUS =
  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent";

/**
 * The highlighted sessions as full-width colour blocks: an outlined numeral,
 * the title, a time chip, the blurb, and a button into "My schedule" that
 * uses the same bookmark hook as the Agenda tab. The Agenda tab holds the whole programme. Renders nothing, and reads no bookmarks, without chapters.
 */
export function ChapterBlocks({
  chapters,
  sessions,
  eventId,
  uid,
  timeZone,
  eventDates,
}: {
  chapters: Chapter[];
  /** Every session with a time, which the chips number their days from. */
  sessions: AgendaItem[];
  eventId: string;
  uid: string;
  timeZone: string;
  eventDates: EventDates;
}) {
  if (chapters.length === 0) return null;
  return (
    <Chapters
      chapters={chapters}
      sessions={sessions}
      eventId={eventId}
      uid={uid}
      timeZone={timeZone}
      eventDates={eventDates}
    />
  );
}

function Chapters({
  chapters,
  sessions,
  eventId,
  uid,
  timeZone,
  eventDates,
}: {
  chapters: Chapter[];
  sessions: AgendaItem[];
  eventId: string;
  uid: string;
  timeZone: string;
  eventDates: EventDates;
}) {
  const bookmarks = useBookmarks(uid, eventId);
  const zone = usableTimeZone(timeZone);
  const days = useMemo(
    () => dayNumbers(sessions, zone, eventDates),
    [sessions, zone, eventDates],
  );
  const byId = useMemo(
    () => new Map(sessions.map((item) => [item.id, item])),
    [sessions],
  );
  const ready = bookmarks.state.status === "success";
  const saved = new Set(
    bookmarks.state.status === "success"
      ? bookmarks.state.data.map((bookmark) => bookmark.agendaItemId)
      : [],
  );

  return (
    <section
      aria-labelledby="overview-chapters-heading"
      className="space-y-6"
      data-testid="overview-chapters"
    >
      <div className="space-y-2.5 pt-6">
        <p className="text-sm font-semibold uppercase tracking-[0.14em] text-lit">
          {SECTION_TITLES.highlights.kicker}
        </p>
        <h2
          id="overview-chapters-heading"
          className="font-display text-3xl font-normal leading-tight wrap-anywhere text-foreground sm:text-4xl"
        >
          {SECTION_TITLES.highlights.title}
        </h2>
        <p className="text-lg leading-snug text-muted-foreground">
          Add them to your schedule, then open the full programme for the rest.
        </p>
      </div>

      {bookmarks.state.status === "error" && (
        <p role="alert" className="text-sm text-destructive-text">
          My schedule could not be loaded: {bookmarks.state.error}
        </p>
      )}
      {bookmarks.mutationError && (
        <p role="alert" className="text-sm text-destructive-text">
          {bookmarks.mutationError}
        </p>
      )}

      <div className="space-y-3">
        {chapters.map((chapter) => {
          const item = byId.get(chapter.id);
          const isSaved = saved.has(chapter.id);
          const label = isSaved ? "Saved to my schedule" : "Add to my schedule";
          return (
            <article
              key={chapter.id}
              data-testid={`chapter-${chapter.id}`}
              data-chapter={chapter.id}
              className={`relative flex min-h-[29rem] flex-col gap-4 overflow-hidden rounded-2xl px-5 pb-9 pt-7 sm:px-8 ${CHAPTER_STYLE[chapter.slot].block}`}
              style={{ containerType: "inline-size" }}
            >
              <span
                aria-hidden="true"
                className={`${CHAPTER_NUMBER_CLASS} ${CHAPTER_NUMBER_SIZE.large} ${CHAPTER_STYLE[chapter.slot].chip}`}
              >
                {chapter.number}
              </span>
              <h3
                className={CHAPTER_TITLE_CLASS}
                style={chapterTitleStyle(chapter.title)}
              >
                {chapter.title}
              </h3>
              {item && (
                <span
                  className={CHAPTER_WHEN_CLASS}
                  data-testid={`chapter-when-${chapter.id}`}
                >
                  {sessionWhen(item, days, zone)}
                </span>
              )}
              {chapter.blurb && (
                <p className="text-lg leading-normal wrap-anywhere text-muted-foreground dark:text-inherit">
                  {chapter.blurb}
                </p>
              )}
              {item && (
                <span
                  className="mt-auto inline-flex self-start pt-1"
                  title={bookmarkTitle({
                    bookmarked: isSaved,
                    ready,
                    pending: bookmarks.pendingIds.has(chapter.id),
                  })}
                >
                  <button
                    type="button"
                    // The visible words first, then which session, so the name
                    // still contains the label (WCAG 2.5.3).
                    aria-label={`${label}: ${chapter.title}`}
                    aria-pressed={isSaved}
                    onClick={() => void bookmarks.toggle(item)}
                    disabled={!ready || bookmarks.pendingIds.has(chapter.id)}
                    data-saved={isSaved ? "true" : undefined}
                    className={`inline-flex min-h-12 items-center gap-2 rounded-full border-2 px-5 text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS} ${
                      CHAPTER_STYLE[chapter.slot].ring
                    } ${
                      isSaved
                        ? "border-current bg-transparent hover:bg-current/10"
                        : CHAPTER_STYLE[chapter.slot].solid
                    }`}
                  >
                    {isSaved ? (
                      <BookmarkCheck className="h-4 w-4" aria-hidden />
                    ) : (
                      <Bookmark className="h-4 w-4" aria-hidden />
                    )}
                    {label}
                  </button>
                </span>
              )}
            </article>
          );
        })}
      </div>

      <p className="px-1 text-sm text-muted-foreground">
        Times are {timezonePlace(timeZone)} time.
      </p>
    </section>
  );
}
